import { useEffect, useRef, useState } from "react";

import { RoleGate } from "@/components/RoleGate";
import { Scanner } from "@/components/Scanner";
import {
  useConferencia,
  useConferirNF,
  useEditarMedicamento,
  useLocais,
  useNFDetalhe,
  useNFs,
  useUploadNF,
  useUploadNFPreview,
} from "@/hooks/useApi";
import { useUser } from "@/hooks/useUser";
import type { ConferenciaItem, NFEntrada, UploadPreview } from "@/types/api";

function brl(value: string | null | undefined): string {
  if (!value) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

const STATUS_NF: Record<string, { label: string; cls: string }> = {
  pendente:   { label: "Aguardando conferência", cls: "badge bg-amber-100 text-amber-800" },
  conferida:  { label: "Conferida", cls: "badge bg-green-100 text-green-800" },
  divergente: { label: "Divergente", cls: "badge bg-anarosa-50 text-anarosa-700" },
};

export function NotasFiscaisPage() {
  return (
    <RoleGate allowed={["admin", "auditor", "compras", "farmaceutico"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const { data: user } = useUser();
  const { data: nfs, isLoading } = useNFs();
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [conferindoId, setConferindoId] = useState<string | null>(null);

  const podeUpload = user && ["admin", "compras", "farmaceutico"].includes(user.papel);
  const podeConferir = user && ["admin", "farmaceutico"].includes(user.papel);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Notas Fiscais de entrada</h2>
          <p className="text-sm text-ink-muted">
            Compras sobe o XML da NF-e → farmácia confere bipando o código de barras e
            escolhe o destino de cada item. O estoque só fica disponível após a conferência.
          </p>
        </div>
      </div>

      {podeUpload ? <UploadCard /> : null}

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {nfs && nfs.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Número/Série</th>
                <th className="px-4 py-3">Fornecedor</th>
                <th className="px-4 py-3">Emissão</th>
                <th className="px-4 py-3">Recebida</th>
                <th className="px-4 py-3 text-right">Valor total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {nfs.map((nf: NFEntrada) => {
                const st = STATUS_NF[nf.status] ?? { label: nf.status, cls: "badge-ordinario" };
                return (
                  <tr key={nf.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{nf.numero}/{nf.serie}</td>
                    <td className="px-4 py-2">{nf.fornecedor.razao_social}</td>
                    <td className="px-4 py-2 text-ink-muted">{formatDate(nf.data_emissao)}</td>
                    <td className="px-4 py-2 text-ink-muted">{formatDate(nf.data_recebimento)}</td>
                    <td className="px-4 py-2 text-right">{brl(nf.valor_total)}</td>
                    <td className="px-4 py-2">
                      <span className={st.cls}>{st.label}</span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                      {nf.status === "pendente" && podeConferir ? (
                        <button
                          className="btn-primary !px-3 !py-1 text-xs"
                          onClick={() => setConferindoId(nf.id)}
                        >
                          📷 Conferir
                        </button>
                      ) : null}
                      <button
                        className="text-sm text-anarosa hover:underline"
                        onClick={() => setDetalheId(nf.id)}
                      >
                        Detalhe
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : nfs && nfs.length === 0 ? (
        <p className="text-sm text-ink-muted">Nenhuma NF carregada ainda.</p>
      ) : null}

      {detalheId ? <Detalhe id={detalheId} onClose={() => setDetalheId(null)} /> : null}
      {conferindoId ? (
        <ConferenciaModal nfId={conferindoId} onClose={() => setConferindoId(null)} />
      ) : null}
    </div>
  );
}

// ---------- Conferência por scan ----------
interface RowState {
  local_id: string;
  qtd: string;
  bipado: boolean;
}

function ConferenciaModal({ nfId, onClose }: { nfId: string; onClose: () => void }) {
  const { data, isLoading } = useConferencia(nfId);
  const { data: locais } = useLocais();
  const conferir = useConferirNF();
  const farmacias = (locais ?? []).filter((l) => l.tipo === "farmacia");
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [destacado, setDestacado] = useState<string | null>(null);
  // Código bipado que não casou: fica pendente pra vincular ao item certo.
  const [codigoPendente, setCodigoPendente] = useState<string | null>(null);
  const editarMed = useEditarMedicamento();

  // Inicializa o estado das linhas quando os dados chegam
  useEffect(() => {
    if (!data) return;
    setRows((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const init: Record<string, RowState> = {};
      for (const it of data.itens) {
        init[it.nf_item_lote_id] = {
          local_id: it.local_destino_id ?? it.local_sugerido_id ?? "",
          qtd: String(it.qtd_recebida ?? it.qtd_nf),
          bipado: false,
        };
      }
      return init;
    });
  }, [data]);

  function update(id: string, patch: Partial<RowState>) {
    setRows((r) => ({ ...r, [id]: { ...r[id], ...patch } }));
  }

  function handleScan(codigo: string) {
    setScannerOpen(false);
    if (!data) return;
    const alvo = data.itens.find(
      (it) => it.codigo_ean === codigo || it.codigo_etiqueta_interna === codigo,
    );
    if (!alvo) {
      // Não casou: guarda o código e deixa a farmácia apontar o item certo,
      // vinculando o EAN ao catálogo (o cEAN da NF-e costuma vir vazio).
      setCodigoPendente(codigo);
      setScanMsg(`Código "${codigo}" ainda não vinculado. Toque em "🔗 é este" no item correspondente pra vincular.`);
      return;
    }
    setScanMsg(null);
    setCodigoPendente(null);
    update(alvo.nf_item_lote_id, { bipado: true });
    setDestacado(alvo.nf_item_lote_id);
    setTimeout(() => setDestacado(null), 2500);
  }

  async function vincular(it: ConferenciaItem) {
    if (!codigoPendente) return;
    try {
      await editarMed.mutateAsync({ id: it.medicamento_id, patch: { codigo_barras_ean: codigoPendente } });
      update(it.nf_item_lote_id, { bipado: true });
      setDestacado(it.nf_item_lote_id);
      setScanMsg(`✅ EAN ${codigoPendente} vinculado a ${it.medicamento_descricao}. Próximos bipes casam sozinhos.`);
      setCodigoPendente(null);
      setTimeout(() => setDestacado(null), 2500);
    } catch { /* erro via editarMed.error */ }
  }

  const todosComLocal = data
    ? data.itens.every((it) => rows[it.nf_item_lote_id]?.local_id)
    : false;
  const algumNaoBipado = data
    ? data.itens.some((it) => !rows[it.nf_item_lote_id]?.bipado)
    : false;
  const algumaDivergencia = data
    ? data.itens.some((it) => Number(rows[it.nf_item_lote_id]?.qtd ?? it.qtd_nf) !== it.qtd_nf)
    : false;

  async function submit() {
    if (!data) return;
    const payload = {
      itens: data.itens.map((it) => ({
        nf_item_lote_id: it.nf_item_lote_id,
        local_id: rows[it.nf_item_lote_id].local_id,
        qtd_recebida: Number(rows[it.nf_item_lote_id].qtd),
      })),
    };
    try {
      await conferir.mutateAsync({ nfId, payload });
      setTimeout(onClose, 2200);
    } catch {
      // erro exibido via conferir.error
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="card max-h-[88vh] w-full max-w-3xl overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-semibold">
              Conferência — NF {data?.numero}/{data?.serie}
            </h3>
            <p className="text-sm text-ink-muted">
              Bipe o código de barras de cada item, escolha a farmácia destino e confirme a quantidade.
            </p>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <button className="btn-primary" onClick={() => setScannerOpen(true)}>
            📷 Bipar item
          </button>
          {scanMsg ? <span className="text-sm text-anarosa">{scanMsg}</span> : null}
        </div>
        <Scanner open={scannerOpen} onScan={handleScan} onClose={() => setScannerOpen(false)} />

        {isLoading || !data ? (
          <p className="text-ink-muted">Carregando…</p>
        ) : (
          <div className="space-y-3">
            {data.itens.map((it: ConferenciaItem) => {
              const st = rows[it.nf_item_lote_id];
              if (!st) return null;
              const divergente = Number(st.qtd) !== it.qtd_nf;
              return (
                <div
                  key={it.nf_item_lote_id}
                  className={`rounded-md border p-3 transition ${
                    destacado === it.nf_item_lote_id
                      ? "border-green-500 bg-green-50"
                      : st.bipado
                        ? "border-green-200"
                        : "border-gray-200"
                  }`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span>
                      {st.bipado ? "✅" : "⬜"}{" "}
                      <code className="text-xs">{it.medicamento_codigo}</code>{" "}
                      <b>{it.medicamento_descricao}</b>
                    </span>
                    <span className="text-xs text-ink-muted">
                      lote <b>{it.numero_lote}</b> · val {it.data_validade.split("-").reverse().join("/")}
                      {" · NF: "}<b>{it.qtd_nf}</b> un
                    </span>
                  </div>
                  {codigoPendente && !st.bipado ? (
                    <button
                      type="button"
                      className="btn-secondary mt-2 px-2 py-1 text-xs"
                      disabled={editarMed.isPending}
                      onClick={() => vincular(it)}
                      title={`Vincular o código ${codigoPendente} a este medicamento no catálogo`}
                    >
                      🔗 é este — vincular EAN {codigoPendente}
                    </button>
                  ) : null}
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_140px]">
                    <select
                      className="input"
                      value={st.local_id}
                      onChange={(e) => update(it.nf_item_lote_id, { local_id: e.target.value })}
                    >
                      <option value="">— farmácia destino —</option>
                      {farmacias.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.descricao}{f.id === it.local_sugerido_id ? " (sugerida)" : ""}
                        </option>
                      ))}
                    </select>
                    <input
                      className={`input ${divergente ? "border-anarosa text-anarosa-700" : ""}`}
                      type="number"
                      min={0}
                      value={st.qtd}
                      onChange={(e) => update(it.nf_item_lote_id, { qtd: e.target.value })}
                      title="Quantidade física recebida"
                    />
                  </div>
                  {divergente ? (
                    <p className="mt-1 text-xs text-anarosa">
                      ⚠️ Diferente da NF ({it.qtd_nf} un) — será marcada divergência.
                    </p>
                  ) : null}
                </div>
              );
            })}

            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
              <button
                className="btn-primary"
                disabled={!todosComLocal || conferir.isPending || conferir.isSuccess}
                onClick={submit}
              >
                {conferir.isPending ? "Concluindo…" : "Concluir conferência"}
              </button>
              {!todosComLocal ? (
                <span className="text-xs text-ink-muted">Escolha a farmácia destino de todos os itens.</span>
              ) : algumNaoBipado ? (
                <span className="text-xs text-amber-700">
                  Há itens não bipados — você pode concluir mesmo assim.
                </span>
              ) : null}
              {algumaDivergencia ? (
                <span className="text-xs text-anarosa">Conferência será registrada como divergente.</span>
              ) : null}
              {conferir.error ? (
                <span className="text-sm text-anarosa">{(conferir.error as Error).message}</span>
              ) : null}
              {conferir.isSuccess ? (
                <span className="text-sm text-green-700">
                  ✅ {conferir.data?.divergente
                    ? "Conferência concluída com divergência registrada."
                    : "NF conferida — estoque disponível."}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Upload ----------
function UploadCard() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<UploadPreview | null>(null);
  const previewMut = useUploadNFPreview();
  const uploadMut = useUploadNF();

  function reset() {
    setFile(null);
    setPreview(null);
    previewMut.reset();
    uploadMut.reset();
    if (inputRef.current) inputRef.current.value = "";
  }

  async function handlePreview(f: File) {
    setFile(f);
    try {
      const p = await previewMut.mutateAsync(f);
      setPreview(p);
    } catch {
      setPreview(null);
    }
  }

  async function handleCommit() {
    if (!file) return;
    try {
      await uploadMut.mutateAsync(file);
      // mantém o resultado visível por alguns segundos
      setTimeout(reset, 4000);
    } catch {
      // erro fica em uploadMut.error
    }
  }

  const podeCommitar =
    preview && !preview.nf_ja_existe && preview.nao_casados.length === 0;

  return (
    <div className="card p-4">
      <h3 className="font-display text-base font-semibold">Carregar nova NF-e</h3>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".xml,application/xml,text/xml"
          className="text-sm"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handlePreview(f);
          }}
          disabled={previewMut.isPending || uploadMut.isPending}
        />
        {previewMut.isPending ? <span className="text-ink-muted text-sm">Analisando XML…</span> : null}
        {previewMut.error ? (
          <span className="text-anarosa text-sm">
            Erro: {(previewMut.error as Error).message}
          </span>
        ) : null}
        {file && !previewMut.isPending ? (
          <button className="btn-secondary" onClick={reset}>Cancelar</button>
        ) : null}
      </div>

      {preview ? <PreviewBlock preview={preview} /> : null}

      {preview ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn-primary"
            disabled={!podeCommitar || uploadMut.isPending}
            onClick={handleCommit}
          >
            {uploadMut.isPending ? "Salvando…" : "Confirmar entrada"}
          </button>
          {!podeCommitar ? (
            <span className="text-xs text-ink-muted">
              {preview.nf_ja_existe
                ? "Esta NF já foi carregada."
                : `${preview.nao_casados.length} item(ns) sem cadastro no catálogo.`}
            </span>
          ) : null}
          {uploadMut.error ? (
            <span className="text-anarosa text-sm">
              {(uploadMut.error as Error).message}
            </span>
          ) : null}
          {uploadMut.isSuccess ? (
            <span className="text-green-700 text-sm">
              ✅ NF salva. {uploadMut.data?.lotes_criados.length} lote(s) criado(s).
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PreviewBlock({ preview }: { preview: UploadPreview }) {
  return (
    <div className="mt-4 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
        <div><span className="text-ink-muted">NF:</span> <b>{preview.numero}/{preview.serie}</b></div>
        <div><span className="text-ink-muted">Valor total:</span> {brl(preview.valor_total)}</div>
        <div><span className="text-ink-muted">Fornecedor:</span> {preview.emit_razao_social}</div>
      </div>
      {preview.nf_ja_existe ? (
        <p className="text-anarosa text-sm">⚠️ Esta NF (chave {preview.chave_acesso.slice(-12)}…) já foi carregada antes.</p>
      ) : null}
      {preview.fornecedor_novo ? (
        <p className="text-sm text-ink-muted">ℹ️ Fornecedor novo — será cadastrado automaticamente.</p>
      ) : null}
      <div>
        <h4 className="text-xs uppercase tracking-wider text-ink-muted mb-1">Itens reconhecidos</h4>
        <ul className="text-sm space-y-1">
          {preview.matches.map((m) => (
            <li key={m.n_item}>
              ✓ <code className="text-xs">{m.medicamento_codigo}</code> {m.medicamento_descricao}
              <span className="text-xs text-ink-muted"> (via {m.matched_by})</span>
            </li>
          ))}
        </ul>
      </div>
      {preview.nao_casados.length > 0 ? (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-anarosa mb-1">Não encontrados no catálogo</h4>
          <ul className="text-sm space-y-1">
            {preview.nao_casados.map((m) => (
              <li key={m.n_item}>
                ✗ {m.descricao_nf}
                <span className="block text-xs text-ink-muted">
                  EAN={m.codigo_ean ?? "—"} · ANVISA={m.codigo_anvisa ?? "—"} · cProd={m.codigo_fornecedor}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ink-muted">
            Cadastre/atualize esses SKUs no catálogo (preencher EAN ou código ANVISA) antes de subir a NF.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ---------- Detalhe ----------
function Detalhe({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useNFDetalhe(id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="card max-h-[85vh] w-full max-w-3xl overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-xl font-semibold">Detalhe da NF</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>
        {isLoading || !data ? (
          <p className="text-ink-muted">Carregando…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-ink-muted">Número/Série:</span> {data.numero}/{data.serie}</div>
              <div><span className="text-ink-muted">Valor total:</span> {brl(data.valor_total)}</div>
              <div className="col-span-2">
                <span className="text-ink-muted">Fornecedor:</span> {data.fornecedor.razao_social} <code className="text-xs">{data.fornecedor.cnpj}</code>
              </div>
              <div><span className="text-ink-muted">Emissão:</span> {formatDate(data.data_emissao)}</div>
              <div><span className="text-ink-muted">Recebida:</span> {formatDate(data.data_recebimento)}</div>
              <div className="col-span-2 text-xs text-ink-muted">
                Chave: <code>{data.chave_acesso}</code>
              </div>
            </div>
            <div>
              <h4 className="font-display text-base font-semibold mb-2">
                Itens ({data.itens.length})
              </h4>
              <div className="space-y-3">
                {data.itens.map((it) => (
                  <div key={it.id} className="rounded-md border border-gray-200 p-3 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div>
                        <code className="text-xs">{it.medicamento_codigo}</code>{" "}
                        <b>{it.medicamento_descricao}</b>
                      </div>
                      <div className="text-ink-muted">
                        Qtd: {it.quantidade} · Unit.: {brl(it.preco_unitario)} · Total: {brl(it.valor_total_linha)}
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      {it.lotes.map((l) => (
                        <div key={l.id} className="rounded bg-gray-50 px-3 py-1.5 text-xs">
                          Lote <b>{l.numero_lote}</b> · qtd {l.quantidade} · validade{" "}
                          <b>{formatDate(l.data_validade)}</b>
                          {l.data_fabricacao ? ` · fab ${formatDate(l.data_fabricacao)}` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
